import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faLocationCrosshairs, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Alert from "react-bootstrap/Alert";

import { MainLayout } from "../layouts/mainLayout";
import { RegexConstants } from "../constants";
import { useApiClient } from "../helpers/useApiClient";
import { CoordinatesBindingModel } from "../apiClient";

const validationSchema = Yup.object().shape({
  postcode: Yup.string()
    .matches(RegexConstants.Postcode, "Please enter a valid UK postcode")
    .required("Valid UK postcode is required"),
});

const POPULAR_LOCATIONS = [
  { city: "London", postcode: "SW1A 1AA" },
  { city: "Manchester", postcode: "M1 1AE" },
  { city: "Birmingham", postcode: "B1 1BB" },
  { city: "Bristol", postcode: "BS1 4DJ" },
  { city: "Leeds", postcode: "LS1 2DS" },
  { city: "Glasgow", postcode: "G1 1XQ" },
];

export const Home = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [failedNearestPostcode, setFailedNearestPostcode] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const formik = useFormik({
    initialValues: { postcode: "" },
    validationSchema,
    onSubmit: () => {
      formik.setSubmitting(true);
      navigate(`/results/${formik.values.postcode.trim().toUpperCase()}`);
    },
  });
  const setValues = formik.setValues;

  const getNearestPostcode = useCallback(
    async (latitude: number, longitude: number) => {
      setIsLocating(true);
      try {
        const response = await apiClient.nearest(new CoordinatesBindingModel({ latitude, longitude }));
        if (response.postcode) {
          setValues({ postcode: response.postcode });
          setFailedNearestPostcode(false);
        }
      } catch {
        setFailedNearestPostcode(true);
      } finally {
        setIsLocating(false);
      }
    },
    [apiClient, setValues, setFailedNearestPostcode]
  );

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setFailedNearestPostcode(true);
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => getNearestPostcode(pos.coords.latitude, pos.coords.longitude),
      () => {
        setFailedNearestPostcode(true);
        setIsLocating(false);
      }
    );
  };

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => getNearestPostcode(pos.coords.latitude, pos.coords.longitude),
      () => {}
    );
  }, [getNearestPostcode]);

  return (
    <MainLayout title="Find Beer & Cider Kegs Near You">
      <Container className="text-center">
        <section className="p-2 p-lg-5">
          <h1 className="kf-hero__title display-4">Find beer &amp; cider kegs near you.</h1>
          <p className="kf-hero__lead lead">
            Community-sourced data about retailers in your area that sell Blade&reg;, PerfectDraft&reg;
            and others.
          </p>

          {failedNearestPostcode && (
            <Alert variant="warning" dismissible onClose={() => setFailedNearestPostcode(false)}>
              Unable to automatically detect your location. Please enter your postcode below.
            </Alert>
          )}

          <div className="kf-search-card">
            <Form onSubmit={formik.handleSubmit}>
              <Form.Group controlId="postcode">
                <InputGroup>
                  <Form.Control
                    type="text"
                    size="lg"
                    className="text-center"
                    name="postcode"
                    placeholder="Enter UK postcode (e.g. SW1A 1AA)"
                    aria-label="Your UK postcode"
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (failedNearestPostcode) setFailedNearestPostcode(false);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.postcode}
                    isInvalid={!!(formik.touched.postcode && formik.errors.postcode)}
                    required
                    autoComplete="postal-code"
                  />
                  <Button
                    type="submit"
                    className="btn-search"
                    size="lg"
                    disabled={formik.isSubmitting || !formik.isValid || !formik.values.postcode}
                    aria-label="Search keg retailers"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </InputGroup>
                {formik.touched.postcode && formik.errors.postcode && (
                  <div className="text-danger small mt-2">{formik.errors.postcode}</div>
                )}
              </Form.Group>
            </Form>

            <button
              type="button"
              className="kf-search-card__location-btn mt-2"
              onClick={handleUseLocation}
              disabled={isLocating}
              aria-label="Use my current location"
            >
              <FontAwesomeIcon icon={isLocating ? faSpinner : faLocationCrosshairs} spin={isLocating} className="me-1" />
              {isLocating ? "Detecting location..." : "Use my current location"}
            </button>
          </div>

          <div className="kf-chips">
            <span className="text-secondary small">Popular:</span>
            {POPULAR_LOCATIONS.map((loc) => (
              <button
                key={loc.city}
                type="button"
                className="kf-chips__chip"
                onClick={() => navigate(`/results/${loc.postcode}`)}
              >
                {loc.city}
              </button>
            ))}
          </div>
        </section>
      </Container>
    </MainLayout>
  );
};
